/**
 * Screen-context registry (#674): the screen the user is looking at, modeled as
 * a small data document the agent can read. A route that wants to be understood
 * richly registers a provider (only /markets does today); every other route
 * falls back to `{ route, params }`.
 *
 * Size discipline: context carries identifiers and filter values only, never
 * grid rows — the agent re-fetches real data through its MCP tools using the
 * ids. A hard character cap backstops that rule.
 */

import { formatResultCount } from "@/shared/utils/formatters";

export interface ScreenContext {
  route: string;
  screenId: string;
  contextVersion: number;
  state: Record<string, unknown>;
}

export interface ScreenContextSnapshot {
  screenId: string;
  contextVersion: number;
  state: Record<string, unknown>;
}

export type ScreenContextProvider = () => ScreenContextSnapshot;

/** Serialized-state budget; oversized states are replaced, never trimmed silently. */
export const MAX_CONTEXT_STATE_CHARS = 4000;

let activeProvider: ScreenContextProvider | null = null;

/**
 * Registers the active route's provider; returns the unregister function.
 * Route components register on mount and unregister on unmount, so at most one
 * provider is active (routes render exclusively).
 */
export function registerScreenContextProvider(provider: ScreenContextProvider): () => void {
  activeProvider = provider;
  return () => {
    if (activeProvider === provider) {
      activeProvider = null;
    }
  };
}

/** Builds the context snapshot for the current location. */
export function getScreenContext(pathname: string, searchStr: string): ScreenContext {
  if (activeProvider) {
    const snapshot = activeProvider();
    let state = snapshot.state;
    if (JSON.stringify(state).length > MAX_CONTEXT_STATE_CHARS) {
      state = { truncated: true, reason: "screen state exceeded the context budget" };
    }
    return {
      route: pathname,
      screenId: snapshot.screenId,
      contextVersion: snapshot.contextVersion,
      state,
    };
  }

  return {
    route: pathname,
    screenId: "generic",
    contextVersion: 1,
    state: { params: Object.fromEntries(new URLSearchParams(searchStr)) },
  };
}

/**
 * Discrete labels for the drawer context chips (route, tab, filter count,
 * result count). Generic routes yield a single route chip.
 */
export function listScreenContextChips(context: ScreenContext): string[] {
  if (context.screenId === "markets") {
    const state = context.state as {
      tab?: string;
      filters?: Record<string, unknown>;
      totalResults?: number;
      totalResultsIsLowerBound?: boolean;
    };
    const filterCount = Object.keys(state.filters ?? {}).length;
    const chips = [context.route];
    if (state.tab) {
      chips.push(formatMarketsTabLabel(state.tab));
    }
    chips.push(`${filterCount} filter${filterCount === 1 ? "" : "s"}`);
    if (typeof state.totalResults === "number") {
      chips.push(
        `${formatResultCount(state.totalResults, state.totalResultsIsLowerBound)} results`,
      );
    }
    return chips;
  }

  return [context.route];
}

function formatMarketsTabLabel(tab: string): string {
  if (tab === "rfqs") return "RFQs";
  return tab.charAt(0).toUpperCase() + tab.slice(1);
}
